import { useEffect, useState, useCallback } from 'react'
import { formatDuration } from '../../types/assessment'

interface TimerProps {
  initialSeconds: number
  onTimeUp: () => void
  isPaused?: boolean
}

export function Timer({ initialSeconds, onTimeUp, isPaused = false }: TimerProps) {
  const [secondsRemaining, setSecondsRemaining] = useState(initialSeconds)

  // Reset timer when initialSeconds changes
  useEffect(() => {
    setSecondsRemaining(initialSeconds)
  }, [initialSeconds])

  const handleTimeUp = useCallback(() => {
    onTimeUp()
  }, [onTimeUp])

  useEffect(() => {
    if (isPaused || secondsRemaining <= 0) return

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          handleTimeUp()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isPaused, secondsRemaining, handleTimeUp])

  // Determine urgency color
  const getTimerColor = () => {
    if (secondsRemaining <= 60) return 'text-red-600 animate-pulse'
    if (secondsRemaining <= 300) return 'text-yellow-600'
    return 'text-gray-700'
  }

  // Warning at 5 minutes and 1 minute
  const showWarning = secondsRemaining === 300 || secondsRemaining === 60

  return (
    <div className="flex items-center gap-2">
      <svg
        className={`w-5 h-5 ${getTimerColor()}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className={`font-mono text-lg font-semibold ${getTimerColor()}`}>
        {formatDuration(secondsRemaining)}
      </span>
      {showWarning && (
        <span className="text-sm text-yellow-600 font-medium">
          {secondsRemaining === 300 ? '5 minutes left!' : '1 minute left!'}
        </span>
      )}
    </div>
  )
}
